import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { VerdictBadge, TierBadge, StatusText, RiskDot } from "@/components/Badges";
import { motion } from "framer-motion";
import { ShieldAlert, ShieldCheck, Clock, Layers, ArrowUpRight } from "lucide-react";

function Stat({ label, value, icon: Icon, accent, testid }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid={testid}
      className="rounded-xl border border-slate-800 bg-slate-900/60 p-5"
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] uppercase tracking-wider text-slate-500">{label}</span>
        <Icon className={`h-4 w-4 ${accent}`} />
      </div>
      <div className="mt-3 font-heading text-4xl font-extrabold tabular-nums text-slate-100">{value}</div>
    </motion.div>
  );
}

export default function Dashboard() {
  const [m, setM] = useState(null);

  useEffect(() => {
    api.get("/dashboard/metrics").then((r) => setM(r.data)).catch(() => {});
  }, []);

  if (!m) return <div className="text-slate-500">Loading metrics…</div>;

  const totalBuckets = m.risk_buckets.low + m.risk_buckets.medium + m.risk_buckets.high || 1;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-100 sm:text-3xl">
          Command Center
        </h1>
        <p className="mt-1 text-sm text-slate-400">
          Live overview of screening activity, verdicts and risk distribution.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total Screenings" value={m.total} icon={Layers} accent="text-blue-400" testid="stat-total" />
        <Stat label="Pending Review" value={m.pending_review} icon={Clock} accent="text-amber-400" testid="stat-pending" />
        <Stat label="Approved" value={m.by_verdict?.APPROVE || 0} icon={ShieldCheck} accent="text-emerald-400" testid="stat-approved" />
        <Stat label="Rejected" value={m.by_verdict?.REJECT || 0} icon={ShieldAlert} accent="text-red-400" testid="stat-rejected" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5">
          <h3 className="font-heading text-base font-semibold text-slate-200">Risk Distribution</h3>
          <div className="mt-4 space-y-3">
            {[
              { k: "low", label: "Low (0–2)", color: "bg-emerald-500", n: m.risk_buckets.low },
              { k: "medium", label: "Medium (3–5)", color: "bg-amber-500", n: m.risk_buckets.medium },
              { k: "high", label: "High (6–10)", color: "bg-red-500", n: m.risk_buckets.high },
            ].map((b) => (
              <div key={b.k} data-testid={`risk-bucket-${b.k}`}>
                <div className="mb-1 flex justify-between text-xs text-slate-400">
                  <span>{b.label}</span>
                  <span className="font-mono tabular-nums">{b.n}</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-800">
                  <div className={`h-full ${b.color}`} style={{ width: `${(b.n / totalBuckets) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 lg:col-span-2">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="font-heading text-base font-semibold text-slate-200">Recent Screenings</h3>
            <Link to="/cases" className="flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300" data-testid="view-all-cases">
              View all <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="space-y-2">
            {m.recent.length === 0 && <div className="text-sm text-slate-500">No screenings yet.</div>}
            {m.recent.map((s) => (
              <Link
                key={s.id}
                to={`/cases/${s.id}`}
                data-testid={`recent-case-${s.id}`}
                className="flex items-center justify-between rounded-lg border border-slate-800 bg-slate-950/50 px-4 py-3 transition-colors hover:border-slate-700"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-slate-200">{s.entity?.name}</div>
                  <div className="font-mono text-[10px] uppercase tracking-wide text-slate-500">
                    {s.entity?.entity_type} · {s.entity?.jurisdiction || "—"}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <RiskDot color={s.risk_color} score={s.risk_score} />
                  <span className="font-mono text-sm font-bold tabular-nums text-slate-300">{s.risk_score}</span>
                  <VerdictBadge verdict={s.verdict} />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
